import {
  DesktopBridgeSessionEventSchema,
  DesktopCapabilityResponseMessageSchema,
  DesktopCloseResponseMessageSchema,
  DesktopFrameMessageSchema,
  DesktopOperationResponseMessageSchema,
  DesktopSessionEventMessageSchema,
  DESKTOP_PROTOCOL_VERSION,
  type DesktopFrameMessage,
} from "../../shared/schemas";
import { activeFrames, type ActiveAppFrame } from "../app-frames";
import { DesktopAdmissionController, postAdmissionFailure } from "./broker-admission";
import { activateDesktopCapability } from "./broker-activation";
import {
  closeError,
  DESKTOP_FRAME_BROKER_DEADLINES_MS,
  getDesktopApi,
  operationError,
  type DesktopPreloadApi,
  type OwnedSession,
  withDeadline,
} from "./broker-core";
import { closeDesktopCapability, performDesktopOperation } from "./broker-operations";

declare global {
  interface Window {
    ccboxDesktop?: unknown;
  }
}

export { DESKTOP_FRAME_BROKER_DEADLINES_MS } from "./broker-core";

export async function closeLocalSessionBestEffort(
  api: DesktopPreloadApi | null,
  sessionHandle: string,
): Promise<void> {
  if (!api) return;
  await withDeadline(
    api.closeCapability({
      protocolVersion: DESKTOP_PROTOCOL_VERSION,
      sessionHandle,
    }),
    DESKTOP_FRAME_BROKER_DEADLINES_MS.close,
    {
      code: "DESKTOP_TIMEOUT",
      message: "CCBox Desktop did not finish local capability cleanup in time.",
    },
  ).catch(() => undefined);
}

export class DesktopFrameBroker {
  readonly #ownedSessions = new Map<string, OwnedSession>();
  readonly #admission = new DesktopAdmissionController();
  readonly #api: DesktopPreloadApi | null;
  readonly #unsubscribe: (() => void) | null;

  constructor() {
    this.#api = getDesktopApi();
    this.#unsubscribe = this.#api
      ? this.#api.subscribe((event) => {
          this.#onDesktopEvent(event);
        })
      : null;
  }

  async handleMessage(event: MessageEvent<unknown>): Promise<boolean> {
    const context = this.#resolveMessage(event);
    if (!context) return false;
    const { source, active, message } = context;
    const ownedCleanup = this.#isOwnedCleanup(message, source, active);
    const admission = this.#admission.admit(source, message.requestId, ownedCleanup);
    if (!admission.admitted) {
      postAdmissionFailure({ source, active, message, failure: admission });
      return true;
    }
    try {
      await this.#handleAdmittedMessage(source, active, message);
      return true;
    } finally {
      this.#admission.settle(source);
    }
  }

  async closeFrame(source: WindowProxy): Promise<void> {
    this.#admission.forget(source);
    const handles = [...this.#ownedSessions.entries()]
      .filter(([, owned]) => owned.source === source)
      .map(([handle]) => handle);
    await Promise.all(
      handles.map(async (sessionHandle) => {
        this.#ownedSessions.delete(sessionHandle);
        await this.#closeLocalSessionBestEffort(sessionHandle);
      }),
    );
  }

  async dispose(): Promise<void> {
    this.#unsubscribe?.();
    const sources = new Set([...this.#ownedSessions.values()].map(({ source }) => source));
    await Promise.all([...sources].map((source) => this.closeFrame(source)));
  }

  #resolveMessage(event: MessageEvent<unknown>): {
    active: ActiveAppFrame;
    message: DesktopFrameMessage;
    source: WindowProxy;
  } | null {
    if (!event.source) return null;
    const source = event.source as WindowProxy;
    const active = activeFrames.get(source);
    if (event.origin !== active?.origin || event.source !== active.iframe.contentWindow)
      return null;
    const parsed = DesktopFrameMessageSchema.safeParse(event.data);
    return parsed.success ? { source, active, message: parsed.data } : null;
  }

  #isOwnedCleanup(
    message: DesktopFrameMessage,
    source: WindowProxy,
    active: ActiveAppFrame,
  ): boolean {
    if (message.type !== "platform:desktop-capability-close") return false;
    const owned = this.#ownedSessions.get(message.sessionHandle);
    return owned?.source === source && owned.frame === active;
  }

  async #handleAdmittedMessage(
    source: WindowProxy,
    active: ActiveAppFrame,
    message: DesktopFrameMessage,
  ): Promise<void> {
    if (message.type === "platform:desktop-capability-request") {
      await this.#activate(source, active, message);
      return;
    }
    const owned = this.#ownedSessions.get(message.sessionHandle);
    if (owned?.source !== source || owned.frame !== active) {
      this.#postMissingSession(source, active, message);
      return;
    }
    if (message.type === "platform:desktop-operation-request") {
      await performDesktopOperation({
        active,
        api: this.#api,
        frameIsCurrent: () => this.#frameIsCurrent(source, active),
        message,
        owned,
        source,
        onStale: () => this.closeFrame(source),
      });
      return;
    }
    await closeDesktopCapability({
      active,
      api: this.#api,
      frameIsCurrent: () => this.#frameIsCurrent(source, active),
      message,
      source,
    });
    this.#ownedSessions.delete(message.sessionHandle);
  }

  async #activate(
    source: WindowProxy,
    active: ActiveAppFrame,
    message: Extract<DesktopFrameMessage, { type: "platform:desktop-capability-request" }>,
  ): Promise<void> {
    const outcome = await activateDesktopCapability({
      active,
      api: this.#api,
      closeSession: (sessionHandle) => this.#closeLocalSessionBestEffort(sessionHandle),
      frameIsCurrent: () => this.#frameIsCurrent(source, active),
      message,
      source,
    });
    if (outcome.stale) return;
    if (outcome.ownedSession && outcome.result.ok) {
      this.#ownedSessions.set(outcome.result.data.sessionHandle, outcome.ownedSession);
    }
    source.postMessage(
      DesktopCapabilityResponseMessageSchema.parse({
        type: "platform:desktop-capability-response",
        protocolVersion: DESKTOP_PROTOCOL_VERSION,
        requestId: message.requestId,
        result: outcome.result,
      }),
      active.origin,
    );
  }

  #postMissingSession(
    source: WindowProxy,
    active: ActiveAppFrame,
    message: Exclude<DesktopFrameMessage, { type: "platform:desktop-capability-request" }>,
  ): void {
    const error = [
      "SESSION_NOT_FOUND",
      "That desktop capability session is unavailable.",
      message.requestId,
    ] as const;
    const response =
      message.type === "platform:desktop-operation-request"
        ? DesktopOperationResponseMessageSchema.parse({
            type: "platform:desktop-operation-response",
            protocolVersion: DESKTOP_PROTOCOL_VERSION,
            requestId: message.requestId,
            result: operationError(...error),
          })
        : DesktopCloseResponseMessageSchema.parse({
            type: "platform:desktop-capability-close-response",
            protocolVersion: DESKTOP_PROTOCOL_VERSION,
            requestId: message.requestId,
            result: closeError(...error),
          });
    source.postMessage(response, active.origin);
  }

  #onDesktopEvent(rawEvent: unknown): void {
    const parsed = DesktopBridgeSessionEventSchema.safeParse(rawEvent);
    if (!parsed.success) return;
    const owned = this.#ownedSessions.get(parsed.data.sessionHandle);
    if (!owned) return;
    if (!this.#frameIsCurrent(owned.source, owned.frame)) {
      void this.closeFrame(owned.source);
      return;
    }
    owned.source.postMessage(
      DesktopSessionEventMessageSchema.parse({
        type: "platform:desktop-session-event",
        protocolVersion: DESKTOP_PROTOCOL_VERSION,
        sessionHandle: parsed.data.sessionHandle,
        event: {
          type: parsed.data.type,
          ...(parsed.data.state ? { state: parsed.data.state } : {}),
          occurredAt: parsed.data.occurredAt,
        },
      }),
      owned.frame.origin,
    );
    if (parsed.data.state && ["revoked", "expired", "closed"].includes(parsed.data.state)) {
      this.#ownedSessions.delete(parsed.data.sessionHandle);
    }
  }

  #frameIsCurrent(source: WindowProxy, frame: ActiveAppFrame): boolean {
    return activeFrames.get(source) === frame && source === frame.iframe.contentWindow;
  }

  async #closeLocalSessionBestEffort(sessionHandle: string): Promise<void> {
    await closeLocalSessionBestEffort(this.#api, sessionHandle);
  }
}
